/**
 * Parcours sans campagne — pseudo-campagne « Hors campagne » (§0.3 du plan v3).
 *
 * `campagneId` est optionnel en base : ces parcours existent, et la suppression
 * de l'écran transverse `/parcours` les rendrait inatteignables. Cet écran est
 * leur seule porte d'entrée.
 *
 * Rien n'est créé en base : l'entrée est virtuelle, la query filtre sur
 * `campagneId === undefined` via l'index composite.
 */
import {
  colonnesParcours,
  FACETTES_PARCOURS,
  RECHERCHE_PARCOURS,
} from "@repo/campagne";
import {
  Button,
  DataTable,
  PageContainer,
  PageHeader,
  useFiltresColonnesUrl,
} from "@repo/ui";
import { useQuery } from "convex/react";
import { Link } from "react-router";
import { api } from "@convex/_generated/api";

/** Colonnes stables : recalculées à chaque rendu, elles invalideraient la table. */
const COLONNES = colonnesParcours();

/** Mêmes filtres que l'onglet Parcours d'une campagne : même table, même barre. */
const CHAMPS_FILTRES = {
  q: { colonne: RECHERCHE_PARCOURS.colonne },
  statut: { colonne: "statut", multi: true },
  lien: { colonne: "lien", multi: true },
  epreuve: { colonne: "epreuves", multi: true },
} as const;

const BARRE_FILTRES = {
  recherche: RECHERCHE_PARCOURS,
  facettes: FACETTES_PARCOURS,
};

export function HorsCampagne() {
  const [filtres, setFiltres] = useFiltresColonnesUrl(CHAMPS_FILTRES);
  // `campagneId` absent = branche « hors campagne » de la query.
  const vue = useQuery(api.parcoursEvaluations.listerParcoursCampagneVue, {});

  return (
    <div className="min-h-screen flex flex-col items-start">
      <PageContainer>
        <div className="mb-6">
          <Link
            to="/campagnes"
            className="text-sm text-muted-foreground hover:underline"
          >
            ← Retour aux campagnes
          </Link>
        </div>

        <PageHeader
          titre="Hors campagne"
          sousTitre={
            vue ? `${vue.total} parcours sans campagne` : "Chargement…"
          }
        />

        {vue?.tropVolumineux && (
          <p className="mb-4 text-sm text-etat-alerte">
            {vue.total} parcours — cette liste approche la limite d'affichage,
            contactez le support.
          </p>
        )}

        <DataTable
          colonnes={COLONNES}
          donnees={vue?.lignes}
          cleColonnes="hors-campagne"
          barreFiltres={BARRE_FILTRES}
          filtres={filtres}
          onFiltresChange={setFiltres}
          hrefLigne={(ligne) =>
            `/p/${ligne._id}?from=/campagnes/hors-campagne`
          }
          vide={{
            titre: "Aucun parcours hors campagne",
            description:
              "Tous les parcours de l'organisation sont rattachés à une campagne.",
          }}
          videFiltre={{
            titre: "Aucun parcours ne correspond",
            description: "Modifiez ou réinitialisez les filtres.",
            action: (
              <Button variant="outline" onClick={() => setFiltres([])}>
                Effacer les filtres
              </Button>
            ),
          }}
        />
      </PageContainer>
    </div>
  );
}
